import { useEffect, useContext } from "react";
import { demoFrames } from "./demoFrames";
import { GlobalContext } from "../../context/GlobalContext";

/* ──────────────────────────── Timing constants ──────────────────────────── */
/** All delay / speed tweaks live here so we can dial-them-in from one spot.  */
export const TIMING = {
    /* Typing simulation -------------------------------------------------- */
    TYPING_DELAY_PER_CHAR_MS: 45,
    USER_REFLECTION_BASE_MS: 450,
    USER_REFLECTION_PER_CHAR_MS: 5,

    /* Assistant “thinking” ---------------------------------------------- */
    TURN_BASE_DELAY_MS: 150,
    TURN_DELAY_PER_CHAR_MS: 45,
    TURN_MIN_DELAY_MS: 1000,
    TURN_MAX_DELAY_MS: 7000,

    /* In-message sequencing --------------------------------------------- */
    ORIGINAL_TO_TL_MS: 500,
    TL_TO_MORPHEME_MS: 400,
    END_OF_MESSAGE_MS: 1500,

    /* Breather after each user send ------------------------------------- */
    POST_USER_MESSAGE_PAUSE_MS: 1200,

    /* Streaming safety-net ---------------------------------------------- */
    STREAM_MIN_CHUNK_MS: 20,

    /* Morpheme table timing --------------------------------------------- */
    MORPHEME_MIN_CHUNK_MS: 140,
    MORPHEME_EXTRA_DWELL_PER_ROW_MS: 250,

    /* One-off helpers ---------------------------------------------------- */
    FIRST_EXPOSURE_PAD_MS: 1800,
    FADE_LAST_PAUSE_MS: 1200,
};

/* ──────────────────────────  Utility helpers  ────────────────────────── */

function sleep(ms) {
    return new Promise((res) => setTimeout(res, ms));
}

function flattenText(value) {
    return Array.isArray(value) ? value.join("") : value || "";
}

/* ──────────────────  TL-stream helpers (motto ×3 support)  ────────────────── */
function resolveTlChunks(frame, tlRaw, hasOriginal) {
    // If the frame defines its own chunk list, honour it (e.g. ["もっと","もっと","もっと"]).
    return frame.customTlChunks ?? (hasOriginal ? tlRaw.split("") : [tlRaw]);
}
function resolveTlChunkDelay(frame, defaultDelay) {
    return frame.tlChunkDelayOverride ?? defaultDelay;
}
function resolveMorphChunks(frame, defaultChunks) {
    // If the frame defines its own chunk list, honour it (e.g. rows with three “もっと”s).
    return frame.customMorphChunks ?? defaultChunks;
}
function resolveMorphChunkDelay(frame, defaultDelay) {
    return frame.morphChunkDelayOverride ?? defaultDelay;
}

/* ────────── Animate user typing with script-switch & word pauses ─────────── */
async function animateUserInput(ref, text, { charDelayOverride, wordPauseList = [] } = {}) {
    if (!ref?.current) return;

    const chars = [...flattenText(text)];
    let current = "";

    // Simple script buckets: Latin (ASCII) vs. Kana/Kanji/CJK
    const script = (ch) => (/[\u3040-\u30FF\u4E00-\u9FFF]/.test(ch) ? "cjk" : "latn");
    const alreadyPausedFor = new Set(); // avoid pausing twice for same word

    for (let i = 0; i < chars.length; i++) {
        const ch = chars[i];
        current += ch;
        ref.current.setValue(current);

        // per-char delay + jitter so it feels human
        const base = charDelayOverride ?? TIMING.TYPING_DELAY_PER_CHAR_MS;
        const jitter = (Math.random() - 0.5) * 30;
        await sleep(base + jitter);

        // EXTRA 300 ms whenever we switch scripts
        if (i < chars.length - 1 && script(ch) !== script(chars[i + 1])) {
            await sleep(300);
        }

        // OPTIONAL pauses after specific words (e.g. "Sore wa", "really")
        for (const word of wordPauseList) {
            if (current.endsWith(word) && !alreadyPausedFor.has(word)) {
                alreadyPausedFor.add(word);
                await sleep(300);
            }
        }
    }
}

/* ────────────────────  Conversation-timing utilities  ──────────────────── */

function getMessageText(frame) {
    if (!frame?.message) return "";
    const { originalMessage, targetLanguageMessage } = frame.message;

    const candidates = [originalMessage?.value, targetLanguageMessage?.value].filter(Boolean);
    if (!candidates.length) return "";

    const text = Array.isArray(candidates[0]) ? candidates[0].join("") : candidates[0];
    return text || "";
}

function computeDynamicDelay(_prevFrame, currentFrame) {
    const textLen = getMessageText(currentFrame).length;
    const morphRows = currentFrame.message?.morphemeTranslationDetails?.value?.length || 0;

    const base =
        textLen * TIMING.TURN_DELAY_PER_CHAR_MS +
        TIMING.USER_REFLECTION_BASE_MS +
        morphRows * TIMING.MORPHEME_EXTRA_DWELL_PER_ROW_MS; // NEW

    const final = base + TIMING.TURN_BASE_DELAY_MS;
    return Math.min(Math.max(final, TIMING.TURN_MIN_DELAY_MS), TIMING.TURN_MAX_DELAY_MS);
}

/** Pause after the user stops typing (longer messages need longer reflection). */
function computeUserPostInputDelay(text) {
    return (
        TIMING.USER_REFLECTION_BASE_MS +
        flattenText(text).length * TIMING.USER_REFLECTION_PER_CHAR_MS
    );
}

function allocateFieldTiming({ frame, totalDelay }) {
    const hasTL = !!frame.message.targetLanguageMessage;
    const hasMorph = !!frame.message.morphemeTranslationDetails;

    const morphRows = hasMorph
        ? Array.isArray(frame.message.morphemeTranslationDetails.value)
            ? frame.message.morphemeTranslationDetails.value.length
            : 1
        : 0;

    /* constant padding before/after streaming */
    const constants =
        (hasTL ? TIMING.ORIGINAL_TO_TL_MS : 0) +
        (hasMorph ? TIMING.TL_TO_MORPHEME_MS : 0) +
        TIMING.END_OF_MESSAGE_MS +
        morphRows * TIMING.MORPHEME_EXTRA_DWELL_PER_ROW_MS; // <- NEW linger

    const loadingBudget = Math.max(totalDelay - constants, 0);

    /* Weighting – 45 % TL, 55 % morphemes */
    const TL_WEIGHT = 0.45;
    const MORPH_WEIGHT = 0.55;
    const totalWeight = (hasTL ? TL_WEIGHT : 0) + (hasMorph ? MORPH_WEIGHT : 0);

    let tlChunkDelay = TIMING.STREAM_MIN_CHUNK_MS;
    let morphChunkDelay = TIMING.MORPHEME_MIN_CHUNK_MS;

    if (hasTL) {
        const tlLen = frame.message.targetLanguageMessage.value.length;
        const tlShare = (TL_WEIGHT / totalWeight) * loadingBudget;
        tlChunkDelay = Math.max(tlShare / tlLen, TIMING.STREAM_MIN_CHUNK_MS);
    }

    if (hasMorph && morphRows) {
        const morphShare = (MORPH_WEIGHT / totalWeight) * loadingBudget;
        morphChunkDelay = Math.max(
            morphShare / morphRows,
            TIMING.MORPHEME_MIN_CHUNK_MS // enforce per-row minimum
        );
    }

    return {
        tlChunkDelay,
        morphChunkDelay,
        constantsDelays: {
            origToTl: hasTL ? TIMING.ORIGINAL_TO_TL_MS : 0,
            tlToMorph: hasMorph ? TIMING.TL_TO_MORPHEME_MS : 0,
            endOfMessage:
                TIMING.END_OF_MESSAGE_MS + morphRows * TIMING.MORPHEME_EXTRA_DWELL_PER_ROW_MS,
        },
    };
}

/**
 * Pads the very first appearance of TL or morpheme translations so
 * the viewer has time to notice a brand-new feature on screen.
 *
 * @param {Object|null} prevFrame          – previous demo frame (or null)
 * @param {Object}       frame             – current demo frame
 * @param {Object}       constantsDelays   – delays from allocateFieldTiming()
 * @returns {Object}                       – same object, possibly adjusted
 */
function applyFirstExposurePad(prevFrame, frame, constantsDelays) {
    /* Frame-level opt-out (e.g. assisted-immersion tagline) */
    if (frame.skipFirstMorphPad) return constantsDelays;

    const firstTL =
        !prevFrame?.message?.targetLanguageMessage &&
        frame.message?.targetLanguageMessage &&
        frame.message?.originalMessage;

    if (firstTL) constantsDelays.origToTl += TIMING.FIRST_EXPOSURE_PAD_MS;

    const firstMorph =
        !prevFrame?.message?.morphemeTranslationDetails &&
        frame.message?.morphemeTranslationDetails;

    if (firstMorph) constantsDelays.tlToMorph += TIMING.FIRST_EXPOSURE_PAD_MS;

    return constantsDelays;
}

/**
 * Incrementally streams the provided `chunks` into `message[fieldName]`
 * while keeping history updates **immutable** so React picks up every
 * single change (essential for CJK char-by-char reveals).
 *
 * NEW: Accepts an optional `perChunkDelays` array to vary the pause
 *      after each individual chunk (e.g. longer waits after “もっと”).
 *
 * @param {Object[]}  history                  – current conversation history
 * @param {Function}  setConversationHistory   – React state setter
 * @param {Object}    message                  – bare message shell already in history
 * @param {"targetLanguageMessage"|
 *         "morphemeTranslationDetails"} fieldName
 * @param {string[]|any[]} chunks              – pieces to stream
 * @param {number}   chunkDelay                – default delay (ms) between chunks
 * @param {number[]} [perChunkDelays=null]     – overrides per-chunk delay
 */
async function streamField({
    history,
    setConversationHistory,
    message,
    fieldName,
    chunks,
    chunkDelay,
    perChunkDelays = null,
}) {
    /** Return a *brand-new* history array with the last message replaced. */
    const pushUpdate = (newMessage) =>
        setConversationHistory((prev) => [...prev.slice(0, -1), newMessage]);

    /* 1️⃣  initialise streaming state */
    let currentMessage = {
        ...message,
        [fieldName]: {
            status: "streaming",
            value: fieldName === "morphemeTranslationDetails" ? [] : "",
        },
    };
    pushUpdate(currentMessage);

    /* 2️⃣  stream chunk-by-chunk */
    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const updatedValue = Array.isArray(currentMessage[fieldName].value)
            ? [...currentMessage[fieldName].value, chunk]
            : currentMessage[fieldName].value + chunk;

        currentMessage = {
            ...currentMessage,
            [fieldName]: {
                ...currentMessage[fieldName],
                value: updatedValue,
            },
        };
        pushUpdate(currentMessage);

        const delay = perChunkDelays ? perChunkDelays[i] : chunkDelay;
        await sleep(delay);
    }

    /* 3️⃣  mark complete */
    currentMessage = {
        ...currentMessage,
        [fieldName]: {
            ...currentMessage[fieldName],
            status: "done",
        },
    };
    pushUpdate(currentMessage);

    return { history: [...history.slice(0, -1), currentMessage], message: currentMessage };
}

/* ───────────────────────────  Main component  ─────────────────────────── */

function DemoController({ setConversationHistory, inputRef }) {
    const { auth } = useContext(GlobalContext);
    const { userId } = auth;

    useEffect(() => {
        let cancelled = false;

        // Clear conversation history before beginning the demo
        setConversationHistory([]);

        async function runDemo() {
            await sleep(1600);

            let history = [];
            let prevFrame = null;

            for (const frame of demoFrames) {
                if (cancelled) return;

                /* ────────────── USER frames ────────────── */
                if (frame.message?.senderType === "user") {
                    /* 1️⃣  Simulated typing */
                    const textTyped =
                        frame.message.originalMessage?.value ??
                        frame.message.targetLanguageMessage?.value ??
                        "";
                    await animateUserInput(inputRef, textTyped, {
                        charDelayOverride: frame.typingDelay ?? undefined,
                        wordPauseList: frame.wordPauseList ?? [],
                    });
                    await sleep(computeUserPostInputDelay(textTyped));

                    /* 2️⃣  Insert shell */
                    let message = {
                        id: frame.message.id,
                        senderType: "user",
                        senderId: userId,
                    };
                    history = [...history, message];
                    setConversationHistory([...history]);
                    if (inputRef?.current) inputRef.current.setValue("");

                    /* 3️⃣  Original sentence (instant) */
                    if (frame.message.originalMessage) {
                        message = {
                            ...message,
                            originalMessage: {
                                status: "loaded",
                                value: flattenText(frame.message.originalMessage.value),
                            },
                        };
                        history[history.length - 1] = message;
                        setConversationHistory([...history]);
                    }

                    /* 4️⃣  Timing allocation + first-exposure pad */
                    const hasOriginal = !!frame.message.originalMessage;
                    const totalBetweenDelay = frame.delay ?? TIMING.POST_USER_MESSAGE_PAUSE_MS;

                    let tlChunkDelay = 0;
                    let morphChunkDelay = 0;
                    let constantsDelays = { origToTl: 0, tlToMorph: 0, endOfMessage: 0 };

                    if (hasOriginal) {
                        ({ tlChunkDelay, morphChunkDelay, constantsDelays } =
                            allocateFieldTiming({ frame, totalDelay: totalBetweenDelay }));
                        constantsDelays = applyFirstExposurePad(
                            prevFrame,
                            frame,
                            constantsDelays
                        );
                    }

                    /* 5️⃣  Target-language sentence */
                    if (frame.message.targetLanguageMessage) {
                        if (hasOriginal) await sleep(constantsDelays.origToTl);

                        const tlRaw = flattenText(frame.message.targetLanguageMessage.value);
                        const tlChunks = resolveTlChunks(frame, tlRaw, hasOriginal);
                        const chunkDelay = resolveTlChunkDelay(frame, tlChunkDelay);

                        ({ history, message } = await streamField({
                            history,
                            setConversationHistory,
                            message,
                            fieldName: "targetLanguageMessage",
                            chunks: tlChunks,
                            chunkDelay,
                        }));
                    }

                    /* 6️⃣  Morpheme translation */
                    if (frame.message.morphemeTranslationDetails) {
                        if (hasOriginal) await sleep(constantsDelays.tlToMorph);

                        /* default rows → each array row is one chunk */
                        const defaultChunks = Array.isArray(
                            frame.message.morphemeTranslationDetails.value
                        )
                            ? frame.message.morphemeTranslationDetails.value
                            : [frame.message.morphemeTranslationDetails.value];

                        /* support custom ordering / slicing */
                        const chunks = resolveMorphChunks(frame, defaultChunks);

                        /* determine per-row delays:
       – baseline = morphChunkDelay (auto-calculated)
       – rows whose surface form === "もっと" get the override (e.g. 800 ms) */
                        const perChunkDelays = chunks.map((row) => {
                            const surface = Array.isArray(row) ? row[0] : row;
                            return surface === "もっと"
                                ? resolveMorphChunkDelay(frame, morphChunkDelay)
                                : morphChunkDelay;
                        });

                        ({ history, message } = await streamField({
                            history,
                            setConversationHistory,
                            message,
                            fieldName: "morphemeTranslationDetails",
                            chunks,
                            chunkDelay: morphChunkDelay, // default
                            perChunkDelays, // NEW
                        }));
                    }

                    /* 7️⃣  End-of-message dwell */
                    await sleep(constantsDelays.endOfMessage);

                    history[history.length - 1] = message;
                    setConversationHistory([...history]);
                }

                /* ────────────── ASSISTANT / mixed frames ────────────── */
                if (frame.message && frame.message.senderType !== "user") {
                    /* 0️⃣  Shell */
                    let message = {
                        id: frame.message.id,
                        senderType: frame.message.senderType,
                        ...(frame.message.senderId && { senderId: frame.message.senderId }),
                    };

                    const totalBetweenDelay =
                        frame.delay ?? computeDynamicDelay(prevFrame, frame);

                    let { tlChunkDelay, morphChunkDelay, constantsDelays } =
                        allocateFieldTiming({ frame, totalDelay: totalBetweenDelay });
                    constantsDelays = applyFirstExposurePad(prevFrame, frame, constantsDelays);

                    const frameStart = Date.now();

                    /* 1️⃣  Original sentence */
                    if (frame.message.originalMessage) {
                        message = {
                            ...message,
                            originalMessage: {
                                status: "loaded",
                                value: flattenText(frame.message.originalMessage.value),
                            },
                        };
                        history = [...history, message];
                        setConversationHistory([...history]);
                    }

                    /* 2️⃣  TL sentence */
                    if (frame.message.targetLanguageMessage) {
                        await sleep(constantsDelays.origToTl);

                        const tlRaw = flattenText(frame.message.targetLanguageMessage.value);
                        const tlChunks = resolveTlChunks(
                            frame,
                            tlRaw,
                            !!frame.message.originalMessage
                        );
                        const chunkDelay = resolveTlChunkDelay(frame, tlChunkDelay);

                        if (frame.message.originalMessage) {
                            ({ history, message } = await streamField({
                                history,
                                setConversationHistory,
                                message,
                                fieldName: "targetLanguageMessage",
                                chunks: tlChunks,
                                chunkDelay,
                            }));
                        } else {
                            // No original sentence → just drop the TL all at once
                            message = {
                                ...message,
                                targetLanguageMessage: { status: "done", value: tlRaw },
                            };
                            history = [...history, message];
                            setConversationHistory([...history]);
                        }
                    }

                    /* 3️⃣  Morpheme table */
                    if (frame.message.morphemeTranslationDetails) {
                        await sleep(constantsDelays.tlToMorph);

                        const defaultChunks = Array.isArray(
                            frame.message.morphemeTranslationDetails.value
                        )
                            ? frame.message.morphemeTranslationDetails.value
                            : [frame.message.morphemeTranslationDetails.value];

                        const chunks = resolveMorphChunks(frame, defaultChunks);

                        const perChunkDelays = chunks.map((row) => {
                            const surface = Array.isArray(row) ? row[0] : row;
                            return surface === "もっと"
                                ? resolveMorphChunkDelay(frame, morphChunkDelay)
                                : morphChunkDelay;
                        });

                        ({ history, message } = await streamField({
                            history,
                            setConversationHistory,
                            message,
                            fieldName: "morphemeTranslationDetails",
                            chunks,
                            chunkDelay: morphChunkDelay, // default
                            perChunkDelays, // NEW
                        }));
                    }

                    /* Persist + dwell */
                    history[history.length - 1] = message;
                    setConversationHistory([...history]);

                    await sleep(constantsDelays.endOfMessage);
                    const elapsed = Date.now() - frameStart;
                    const leftover = totalBetweenDelay - elapsed;
                    if (leftover > 0) await sleep(leftover);
                }

                /* ───────── Inter-frame pause (after user turn) ───────── */
                if (frame.message?.senderType === "user") {
                    const pause = frame.delay ?? TIMING.POST_USER_MESSAGE_PAUSE_MS;
                    await sleep(pause);
                }

                prevFrame = frame;
            }
        }

        runDemo();
        return () => {
            cancelled = true;
        };
    }, [setConversationHistory, inputRef, userId]);

    return null;
}

export default DemoController;
